import { Component, h } from '@stencil/core';
import { autocomplete, getAlgoliaFacets, getAlgoliaResults } from '@algolia/autocomplete-js';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import Cookies from 'js-cookie';
import { autoCompleteFacets, indexName, isBrowser, searchClient, showFourHitsPerPage, showSixHitsPerPage, suggestedQueriesIndexName } from '../../constants/constants';
import { handleAutocompleteSelection, passThruSearchParams } from '../../utils/algoliaAutoComplete';
import { getQueryParamValue, getImageUrl } from '../../utils/url';

@Component({
  tag: 'ci-search',
  styleUrl: 'ci-search.scss',
})
export class ciSearch {
  private setupSearch() {
    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'ALG_CAT_RECENT_SEARCH',
      limit: 6,
      transformSource({ source }) {
        return {
          ...source,
          onSelect: ({ item }) => handleAutocompleteSelection(searchClient, indexName, item.label),
          templates: {
            header() {
              return 'Recent Searches';
            },
            ...source.templates,
          },
        };
      },
    });

    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
      searchClient,
      indexName: suggestedQueriesIndexName,
      getSearchParams({ state }) {
        return {
          hitsPerPage: state.query ? showSixHitsPerPage : showFourHitsPerPage,
        };
      },

      transformSource({ source }) {
        return {
          ...source,
          onSelect: ({ item }) => handleAutocompleteSelection(searchClient, indexName, item.query),
          templates: {
            header() {
              return 'Suggestions';
            },
            ...source.templates,
          },
        };
      },
    });
    const oeUserToken = Cookies.get('oe');
    if (oeUserToken && typeof window.aa === 'function') {
      window.aa('setUserToken', oeUserToken.substring(0, 64));
    }

    autocomplete({
      container: '#autocomplete',
      plugins: [recentSearchesPlugin, querySuggestionsPlugin],
      openOnFocus: true,
      insights: true,
      placeholder: 'Search for t-shirts, hoodies, koozies, and more',
      initialState: {
        query: getQueryParamValue('keyword'),
      },
      getSources({ query }) {
        const searchParams = new URLSearchParams(document.location.search);
        const compatibleTypeFilter = searchParams.get('compatible_type');
        let mappedFilters = '(status:active)';
        let facetFilters = [];
        if (compatibleTypeFilter) facetFilters.push(`compatible_type:${compatibleTypeFilter}`);
        const sources = [];

        autoCompleteFacets.forEach(facet => {
          sources.push({
            sourceId: `facet_${facet.name}`,
            getItems({ query }) {
              if (!query) return [];
              return getAlgoliaFacets({
                searchClient,
                queries: [
                  {
                    indexName: indexName,
                    facet: facet.name,
                    params: {
                      facetQuery: query,
                      maxFacetHits: facet.showNum || 6,
                      clickAnalytics: true,
                    },
                  },
                ],
              });
            },

            templates: {
              header({ items }) {
                if (!items.length) return null;
                return facet.caption;
              },
              item({ item, html, components }) {
                return html`<div>
                  <div class="aa-ItemTitle">${(<components.Highlight hit={item} attribute="label" tagName="mark" />)}</div>
                </div> `;
              },
            },
            onSelect: ({ item }) => handleAutocompleteSelection(searchClient, indexName, item.label),
          });
        });
        sources.push({
          sourceId: 'styles',
          getItems() {
            if (!query) return [];
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: indexName,
                  query,
                  params: {
                    hitsPerPage: 6,
                    filters: mappedFilters,
                    facetFilters: facetFilters,
                    clickAnalytics: true,
                  },
                },
              ],
            });
          },
          templates: {
            header({ items }) {
              if (!items.length) return null;
              return 'Results for Styles';
            },
            item({ item, html, components }) {
              return html` <a href=${item.url} class="aa-ItemLink" style-option status-${item.status}>
                <div class="aa-Item-Content">
                  <div style=${{ margin: '0 auto 5px' }}>
                    <img alt="Style Image" height="140" src=${getImageUrl(item)} />
                  </div>
                  <div class="aa-ItemTitle">${(<components.Highlight hit={item} attribute="name" tagName="mark" />)}</div>
                  <div><ci-Rating ratingScore=${item.rating_score} ratingCount=${item.rating_count} name=${item.name} generalComments=${item.general_comments} /></div>
                </div>
              </a>`;
            },
            footer() {},
            noResults() {
              if (!query) return;
              const multipleNoResults = Array.from(document.querySelectorAll('.aa-SourceNoResults'));
              multipleNoResults.slice(1).forEach(element => element.remove());
              return 'No results';
            },
          },
          onSelect({ item }) {
            let path = '';
            if (item.target_url) path = item.target_url;
            if (item.breadcrumbs && item.breadcrumbs.length) {
              path = item.breadcrumbs[item.breadcrumbs.length - 1].path;
            }
            window.location = passThruSearchParams(path);
          },
        });
        return sources;
      },
    });
  }
  handleKeyDown(evt: KeyboardEvent) {
    const target = evt.target as HTMLInputElement;
    if (target.matches('.aa-Input')) {
      if (evt.key === 'Enter') {
        const qry = (evt.target as HTMLInputElement).value;
        if (qry.trim() === '') return;
        handleAutocompleteSelection(searchClient, indexName, qry.trim());
      }
      if (evt.key === ' ') {
        const el = evt.target as HTMLInputElement;
        let qry = el.value + evt.key;
        // remove 2+ spaces
        while (qry.indexOf('  ') >= 0) {
          qry = qry.replace(/  /g, ' ');
        }
        if (qry.endsWith(' ')) qry = qry.substring(0, qry.length - 1);
        el.value = qry;
      }
    }
  }
  componentDidLoad() {
    if (isBrowser) {
      this.setupSearch();
      document.addEventListener('keydown', this.handleKeyDown);
    }
  }
  render() {
    return (
      <div class="ciSearch">
        <div id="autocomplete"></div>
      </div>
    );
  }
}
